import { useState, useEffect, useCallback } from 'react';

type ThemeChoice = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'planacle_theme';

function getSystemTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function applyTheme(choice: ThemeChoice) {
  const root = document.documentElement;
  if (choice === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', choice);
  }
}

/**
 * M-379: Apply the stored theme choice synchronously before first paint.
 * Called once in main.tsx — prevents the flash where every route loads without
 * data-theme until Profile mounts useTheme.
 * Only forces data-theme when the user has explicitly chosen dark/light;
 * unset and system users keep OS preference via the CSS @media fallback.
 */
export function bootstrapTheme(): void {
  const stored = localStorage.getItem(STORAGE_KEY) as ThemeChoice | null;
  if (stored === 'dark' || stored === 'light') {
    document.documentElement.setAttribute('data-theme', stored);
  } else if (stored === 'system') {
    document.documentElement.removeAttribute('data-theme');
  }
  // null: no stored preference — CSS :root dark default + OS @media handles it
}

/**
 * H-33: Theme hook — Dark / Light / System toggle.
 * Persists to localStorage. Applies `data-theme` attribute to <html>.
 * Falls back to OS preference when set to "system".
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeChoice | null;
    // Default to 'system' (follow the OS) when the user hasn't explicitly chosen — matching
    // bootstrapTheme's "no stored preference → OS @media handles it" behaviour. Defaulting to
    // 'dark' here previously persisted 'dark' on first mount, which pinned the app (and the
    // public guest flow) to dark regardless of the OS light/dark setting.
    return stored ?? 'system';
  });

  // Apply on mount + whenever choice changes
  useEffect(() => {
    applyTheme(choice);
    localStorage.setItem(STORAGE_KEY, choice);
  }, [choice]);

  // Listen for OS preference changes when set to "system"
  useEffect(() => {
    if (choice !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [choice]);

  const setTheme = useCallback((t: ThemeChoice) => setChoice(t), []);

  const resolvedTheme: 'dark' | 'light' = choice === 'system' ? getSystemTheme() : choice;

  return { theme: choice, resolvedTheme, setTheme } as const;
}
